import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, Paper,
  Button, Stack, Chip, IconButton, Tooltip, Alert, CircularProgress, Grid, Card, CardContent,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  ADMIN_ALL_BUDGET_CATEGORIES_QUERY, BUDGET_OVERVIEW_QUERY,
  CREATE_BUDGET_CATEGORY_MUTATION, UPDATE_BUDGET_CATEGORY_MUTATION, DELETE_BUDGET_CATEGORY_MUTATION,
} from '../graphql/queries';
import { useFamily } from '../context/FamilyContext';
import BudgetCategoryDialog from '../components/BudgetCategoryDialog';
import { BudgetStatusChip, BudgetBar, budgetSourceText, inr } from '../components/BudgetStatus';

// Admin > Budgets: every bride/groom family's budget at a glance; with a family selected,
// that family's budget categories (started from the services they selected) to manage.
export default function AdminBudgetCategoriesPage() {
  const navigate = useNavigate();
  const { family, setFamily } = useFamily();
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState(null);
  const [dialogError, setDialogError] = useState('');

  const { data: ovData, loading: ovLoading } = useQuery(BUDGET_OVERVIEW_QUERY, { skip: Boolean(family), fetchPolicy: 'cache-and-network' });
  const { data, loading, refetch } = useQuery(ADMIN_ALL_BUDGET_CATEGORIES_QUERY, { skip: !family, fetchPolicy: 'cache-and-network' });
  const done = (text) => { setDialog(false); setEditing(null); setDialogError(''); refetch(); setMessage({ severity: 'success', text }); };
  const [create, { loading: creating }] = useMutation(CREATE_BUDGET_CATEGORY_MUTATION, { onCompleted: () => done('Budget category added.'), onError: e => setDialogError(e.message) });
  const [update, { loading: updating }] = useMutation(UPDATE_BUDGET_CATEGORY_MUTATION, { onCompleted: () => done('Budget updated.'), onError: e => setDialogError(e.message) });
  const [remove] = useMutation(DELETE_BUDGET_CATEGORY_MUTATION, {
    onCompleted: () => { refetch(); setMessage({ severity: 'info', text: 'Budget category deleted.' }); },
    onError: e => setMessage({ severity: 'error', text: e.message }),
  });

  const families = ovData?.budgetOverview || [];
  const categories = data?.allBudgetCategories || [];
  const totals = categories.reduce((t, c) => ({ budget: t.budget + c.budgetAmount, spent: t.spent + c.totalActual, paid: t.paid + c.totalPaid }), { budget: 0, spent: 0, paid: 0 });
  const attention = families.filter(f => f.status !== 'ok' || f.overdueCount > 0);

  const openFamily = (f) => setFamily({ id: f.familyUserId, displayName: f.familyName, email: f.email, roleDisplayName: f.roleName, serviceCategories: [] });

  const save = (vars) => {
    if (editing) update({ variables: { id: editing.id, ...vars } });
    else create({ variables: vars });
  };

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Budgets</Typography>
          <Typography variant="body2" color="text.secondary">
            {family
              ? `${family.displayName}'s budget — categories start from the services selected; spending is monitored with alerts at 80% and over budget.`
              : 'Every bride/groom family\'s budget, monitored. Select a family above (or Manage below) to work on its budget.'}
          </Typography>
        </Box>
        {family && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => navigate('/dashboard/budget')}>Items & payments</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setDialogError(''); setDialog(true); }}>Add category</Button>
          </Stack>
        )}
      </Stack>

      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}

      {/* All families */}
      {!family && (
        <>
          {attention.length > 0 && (
            <Alert severity={attention.some(f => f.status === 'over') ? 'error' : 'warning'} sx={{ mb: 2 }}>
              {attention.length} famil{attention.length === 1 ? 'y needs' : 'ies need'} attention:{' '}
              {attention.map(f => `${f.familyName} (${[f.overCount && `${f.overCount} over`, f.nearCount && `${f.nearCount} nearly used`, f.overdueCount && `${f.overdueCount} overdue payment(s)`].filter(Boolean).join(', ') || f.status})`).join('; ')}
            </Alert>
          )}
          {ovLoading && !ovData ? <CircularProgress /> : (
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                    <TableCell>Family</TableCell>
                    <TableCell>Budget</TableCell>
                    <TableCell sx={{ width: 260 }}>Spent</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Alerts</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {families.map(f => (
                    <TableRow key={f.familyUserId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{f.familyName}</Typography>
                        <Typography variant="caption" color="text.secondary">{f.roleName} · {f.categoryCount} categor{f.categoryCount === 1 ? 'y' : 'ies'}</Typography>
                      </TableCell>
                      <TableCell>{inr(f.totalBudget)}</TableCell>
                      <TableCell><BudgetBar dense spent={f.totalSpent} budget={f.totalBudget} status={f.status} usedPercent={f.usedPercent} /></TableCell>
                      <TableCell>{inr(f.totalPaid)}</TableCell>
                      <TableCell><BudgetStatusChip status={f.status} /></TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {f.overCount > 0 && <Chip size="small" color="error" label={`${f.overCount} over`} />}
                          {f.nearCount > 0 && <Chip size="small" color="warning" label={`${f.nearCount} nearly used`} />}
                          {f.overdueCount > 0 && <Chip size="small" color="error" variant="outlined" label={`${f.overdueCount} overdue`} />}
                          {!f.overCount && !f.nearCount && !f.overdueCount && <Typography variant="caption" color="text.secondary">—</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell align="right"><Button size="small" onClick={() => openFamily(f)}>Manage</Button></TableCell>
                    </TableRow>
                  ))}
                  {families.length === 0 && <TableRow><TableCell colSpan={7} align="center">No bride/groom families yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {/* One family */}
      {family && (
        <>
          <Grid container spacing={2} mb={2}>
            {[['Budget', totals.budget], ['Spent', totals.spent], ['Paid', totals.paid], [totals.budget - totals.spent < 0 ? 'Over by' : 'Remaining', Math.abs(totals.budget - totals.spent)]].map(([label, value]) => (
              <Grid item xs={6} sm={3} key={label}>
                <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: '12px !important' }}>
                  <Typography variant="h6" fontWeight={700} color={label === 'Over by' ? 'error.main' : 'text.primary'}>{inr(value)}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>
          {loading && !data ? <CircularProgress /> : (
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                    <TableCell>Category</TableCell>
                    <TableCell>Budget</TableCell>
                    <TableCell sx={{ width: 280 }}>Spent</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map(c => (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c.color || '#4a90e2', flexShrink: 0 }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{budgetSourceText(c)}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {inr(c.budgetAmount)}
                        {!c.hasOwnBudget && <Typography variant="caption" color="warning.main" display="block">not set — using items</Typography>}
                      </TableCell>
                      <TableCell><BudgetBar spent={c.totalActual} budget={c.budgetAmount} status={c.status} usedPercent={c.usedPercent} /></TableCell>
                      <TableCell>{inr(c.totalPaid)}</TableCell>
                      <TableCell><BudgetStatusChip status={c.status} /></TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit budget"><IconButton size="small" onClick={() => { setEditing(c); setDialogError(''); setDialog(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove({ variables: { id: c.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {categories.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center">
                      {family.displayName} has no budget yet — it starts when they select services, or add a category.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      <BudgetCategoryDialog
        open={dialog} editing={editing} familyName={family?.displayName}
        existingServices={categories.map(c => c.serviceCategory).filter(Boolean)}
        error={dialogError} busy={creating || updating}
        onClose={() => { setDialog(false); setEditing(null); }} onSave={save}
      />
    </Box>
  );
}
